import { ASSISTANT_ERROR_CODES, UI_CONTEXT_KEYS } from './constants';
import {
    formatConversation,
    formatConversationDetail,
    formatConversationsResponse,
    formatEscalation,
    formatMessage,
} from './format';

import { CHAT_API_URL } from '#config/runtimeconfig';
import Logger from '/b2b/common/helpers/Logger';
import { currentSurface } from './surfaces';
import { getChatToken } from '/b2b/common/helpers/ChatAuth';
import { validate as isUuid } from 'uuid';
import { locale } from '/b2b/common/helpers/i18n';

/*
 * The assistant's data layer, served entirely by chat-api.
 *
 * Calls go browser-direct with an auth-service-minted Bearer (see ChatAuth) and
 * deliberately carry no `x-osno-*` headers — chat-api resolves the caller from the
 * token alone, and the tenant comes from its verified customerId claim.
 *
 * Contract: Chat Service — API Specification
 * https://osano.atlassian.net/wiki/spaces/OE/pages/2040528900
 */

const DEFAULT_PAGE_SIZE = 50;
// chat-api's own page ceiling, for both conversations and messages.
const MAX_PAGE_SIZE = 100;

const uiContext = () => {
    const current = currentSurface();
    if (!current) return null;

    const context = { [UI_CONTEXT_KEYS.ENTITY_TYPE]: current.surface };
    if (current.recordId) {
        context[UI_CONTEXT_KEYS.RECORD] = { [UI_CONTEXT_KEYS.ID]: current.recordId };
    }
    return context;
};

const messageRequestBody = (content, stream) => {
    const context = uiContext();
    return {
        content,
        stream,
        metadata: { locale: locale() },
        ...(context ? { [UI_CONTEXT_KEYS.REQUEST]: context } : {}),
    };
};

/*
 * chat-api pages messages oldest-first with a forward-only cursor, while the widget
 * expects the whole recent transcript up front (its "load earlier" affordance pages
 * backwards). So opening a conversation walks the cursor to the end. One request
 * covers any conversation under 100 messages; the cap bounds the pathological case.
 */
const MAX_MESSAGE_PAGES = 20;

/**
 * chat-api's HTTP statuses mapped onto the assistant's existing error vocabulary, so
 * the error banner and its retry affordance keep working with no new copy.
 * @param {number} status
 * @returns {string}
 */
export const errorCodeForStatus = status => {
    switch (status) {
        case 400:
        case 413:
        case 422:
            return ASSISTANT_ERROR_CODES.AE0001;
        case 401:
            return ASSISTANT_ERROR_CODES.AE0002;
        case 403:
            return ASSISTANT_ERROR_CODES.AE0003;
        case 404:
            return ASSISTANT_ERROR_CODES.AE0004;
        case 429:
            return ASSISTANT_ERROR_CODES.AE0005;
        default:
            return ASSISTANT_ERROR_CODES.AE0006;
    }
};

/**
 * Rejections carry `{ message, errorCode }` because that is what the provider's
 * SET_ERROR and the ChatThread banner read.
 * @param {string} message
 * @param {string} errorCode
 * @param {number} [status]
 */
const chatApiError = (message, errorCode, status) =>
    Object.assign(new Error(message), { name: 'ChatApiError', errorCode, status });

/**
 * A mint failure is an auth failure, not a chat-api failure — carry its own status
 * through so a missing `osano-ai:*` scope reads as "forbidden" rather than "offline".
 * @param {Error & {status?: number}} error
 */
const tokenError = error =>
    chatApiError(
        error.message,
        error.status ? errorCodeForStatus(error.status) : ASSISTANT_ERROR_CODES.AE0002,
        error.status
    );

const withToken = async (options = {}) => {
    let token;
    try {
        token = await getChatToken(options);
    } catch (error) {
        throw tokenError(error);
    }
    return token;
};

/**
 * chat-api errors share one envelope: `{error, message, details, request_id, timestamp}`.
 * @param {Response} response
 * @returns {Promise<Error>}
 */
const readError = async response => {
    let message = `Request failed with status ${response.status}`;
    try {
        const body = await response.json();
        if (body?.message) message = body.message;
    } catch {
        // Body was empty or not JSON — the status alone is the story.
    }
    return chatApiError(message, errorCodeForStatus(response.status), response.status);
};

const buildQuery = params => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') query.append(key, value);
    });
    const serialized = query.toString();
    return serialized ? `?${serialized}` : '';
};

/**
 * Issues one authenticated request against chat-api, re-minting and retrying exactly
 * once on a 401 so an early-revoked or clock-skewed token self-heals without the user
 * seeing anything.
 *
 * @param {string} path Path below the chat-api origin, including `/v1`
 * @param {object} [options] fetch options; `body` is serialized for you
 * @returns {Promise<Response>}
 */
const chatApiFetch = async (path, options = {}) => {
    const { body, headers, ...rest } = options;

    const request = async forceRefresh => {
        const token = await withToken(forceRefresh ? { forceRefresh: true } : undefined);
        try {
            return await fetch(`${CHAT_API_URL}${path}`, {
                ...rest,
                headers: {
                    'Content-Type': 'application/json',
                    ...(headers || {}),
                    Authorization: `Bearer ${token}`,
                },
                ...(body === undefined ? {} : { body: JSON.stringify(body) }),
            });
        } catch (error) {
            // fetch only rejects on transport failure; HTTP errors resolve.
            if (error?.name === 'AbortError') throw error;
            Logger.error(`chat-api request to ${path} failed:`, error.message);
            throw chatApiError(error.message, ASSISTANT_ERROR_CODES.AE0006);
        }
    };

    const response = await request(false);
    if (response.status !== 401) return response;
    return request(true);
};

/**
 * @param {string} path
 * @param {object} [options]
 * @returns {Promise<any>} Parsed JSON body, or null for a 204
 */
const chatApiJson = async (path, options) => {
    const response = await chatApiFetch(path, options);
    if (!response.ok) throw await readError(response);
    if (response.status === 204) return null;
    return response.json();
};

/**
 * Lists the caller's conversations, newest-first. `mode` is ignored: chat-api keeps one
 * list, and escalated conversations are told apart by their `escalation` field.
 *
 * @param {string} [_mode] Ignored
 * @param {object} [options]
 * @param {number} [options.page]
 * @param {number} [options.pageSize]
 * @returns {Promise<object>}
 */
export const fetchConversations = (_mode, { page, pageSize } = {}) => {
    const limit = Math.min(pageSize || DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE);
    const offset = page && page > 1 ? (page - 1) * limit : 0;
    return chatApiJson(`/v1/conversations${buildQuery({ limit, offset })}`).then(
        formatConversationsResponse
    );
};

/**
 * Loads a conversation with its transcript, following chat-api's forward cursor to
 * the newest message (see MAX_MESSAGE_PAGES). The returned pagination always reports
 * no further page, so the widget's backwards "load earlier" affordance stays hidden.
 *
 * @param {string} conversationId
 * @param {object} [options]
 * @param {string} [options.cursor] Start the walk part-way through
 * @param {number} [options.pageSize]
 * @returns {Promise<object>}
 */
export const fetchConversationById = async (conversationId, { cursor, pageSize } = {}) => {
    const limit = Math.min(pageSize || MAX_PAGE_SIZE, MAX_PAGE_SIZE);
    let nextCursor = cursor;
    let conversation = null;
    const messages = [];

    for (let page = 0; page < MAX_MESSAGE_PAGES; page += 1) {
        const body = await chatApiJson(
            `/v1/conversations/${conversationId}${buildQuery({ limit, cursor: nextCursor })}`
        );
        const formatted = formatConversationDetail(body);
        conversation = formatted;
        messages.push(...formatted.messages);
        nextCursor = formatted.messagePagination.nextCursor;
        if (!nextCursor) break;
    }

    if (nextCursor) {
        Logger.warn(
            `Conversation ${conversationId} exceeds ${MAX_MESSAGE_PAGES * limit} messages; the oldest are shown without a page-forward affordance.`
        );
    }

    return {
        ...conversation,
        messages,
        messagePagination: { nextCursor: null, hasNext: false, pageSize: null },
    };
};

/**
 * Appends a message and waits for the turn to finish. The widget streams instead (see
 * sendChatMessageStream).
 *
 * @param {string} message
 * @param {string|null} conversationId
 * @returns {Promise<object>} The AI reply, or the persisted message when no turn was due
 */
export const sendChatMessage = (message, conversationId) =>
    chatApiJson('/v1/messages', {
        method: 'POST',
        headers: conversationId ? { 'X-Osano-Conversation-Id': conversationId } : undefined,
        body: messageRequestBody(message, false),
    }).then(formatMessage);

/**
 * Parses one SSE block into its event name and JSON payload; chat-api emits named
 * events.
 * @param {string} block
 * @returns {{event: string, data: object|null}|null}
 */
const parseEventBlock = block => {
    let event = 'message';
    const dataLines = [];
    for (const line of block.split('\n')) {
        if (line.startsWith(':')) continue; // comment / heartbeat
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (!dataLines.length) return null;
    const raw = dataLines.join('\n');
    try {
        return { event, data: JSON.parse(raw) };
    } catch {
        return { event, data: null };
    }
};

/**
 * Sends a message and streams the AI turn.
 *
 * chat-api's stream carries named events: `message` (a persisted message, opening
 * with the echoed customer message and closing with the reply), `status`
 * (`generating` / `done` / `errored`) and `delta` (reply increments). Two cases leave
 * no turn due — an escalated conversation, where the message is mirrored to the
 * Privacy Team's ticket instead of answered, and a non-customer author — and both
 * end the stream after the echoed message; `onNoReply` reports that so the caller can
 * drop the placeholder it was holding for a reply.
 *
 * @param {string} message
 * @param {string|null} conversationId
 * @param {object} options
 * @param {AbortSignal} [options.signal]
 * @param {function} [options.onToken] Called with each content increment
 * @param {function} [options.onReasoning] Called with each reasoning increment
 * @param {function} [options.onCitations] Called with citation increments
 * @param {function} [options.onAccess] Called with the reply's raw `metadata.access`
 * @param {function} [options.onConversationId] Called with the server's conversation id
 * @param {function} [options.onMessageId] Called with the reply's server-minted id
 * @param {function} [options.onDone] Called when the turn completes
 * @param {function} [options.onNoReply] Called when the stream ends with no reply due
 * @param {function} [options.onError] Called with `{message, errorCode}`
 * @returns {Promise<void>}
 */
export const sendChatMessageStream = async (message, conversationId, options = {}) => {
    const {
        signal,
        onToken,
        onReasoning,
        onCitations,
        onAccess,
        onConversationId,
        onMessageId,
        onDone,
        onNoReply,
        onError,
    } = options;

    let response;
    try {
        response = await chatApiFetch('/v1/messages', {
            method: 'POST',
            headers: conversationId ? { 'X-Osano-Conversation-Id': conversationId } : undefined,
            body: messageRequestBody(message, true),
            signal,
        });
    } catch (error) {
        if (error?.name === 'AbortError') return;
        if (onError) onError({ message: error.message, errorCode: error.errorCode });
        return;
    }

    if (!response.ok) {
        const error = await readError(response);
        if (onError) onError({ message: error.message, errorCode: error.errorCode });
        return;
    }

    // A 200 with no readable body would otherwise throw past onError, leaving the
    // caller's placeholder spinning with nothing to report.
    if (!response.body) {
        if (onError)
            onError({
                message: 'The reply could not be read',
                errorCode: ASSISTANT_ERROR_CODES.AE0007,
            });
        return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    // The turn is "answered" once a reply arrives; anything else is a no-turn stream.
    let sawReply = false;
    let finished = false;
    let reportedConversationId = false;

    const handleEvent = ({ event, data }) => {
        if (!data) return;

        /*
         * Every event carries the conversation id and it cannot change mid-stream, so
         * report it once. Callers treat it as "this is the id your new conversation
         * got", which is a move they can only make once.
         */
        if (data.conversation_id && onConversationId && !reportedConversationId) {
            reportedConversationId = true;
            onConversationId(data.conversation_id);
        }

        if (event === 'message') {
            // The echoed customer message opens the stream; a non-customer message
            // closes an answered turn.
            if (data.user_type && data.user_type !== 'customer') {
                sawReply = true;
                if (data.id && onMessageId) onMessageId(data.id);
                if (data.citations?.length && onCitations) onCitations(data.citations);
                if (data.metadata?.access && onAccess) onAccess(data.metadata.access);
            }
            return;
        }

        if (event === 'status') {
            if (data.message_id && onMessageId) onMessageId(data.message_id);
            if (data.state === 'generating') sawReply = true;
            if (data.state === 'done') {
                finished = true;
                if (onDone) onDone();
            }
            if (data.state === 'errored') {
                finished = true;
                sawReply = true;
                if (onError) {
                    onError({
                        message: data.error || 'The reply could not be generated',
                        errorCode: ASSISTANT_ERROR_CODES.AE0007,
                    });
                }
            }
            return;
        }

        if (event === 'delta') {
            sawReply = true;
            if (data.content && onToken) onToken(data.content);
            if (data.reasoning && onReasoning) onReasoning(data.reasoning);
            if (data.citations?.length && onCitations) onCitations(data.citations);
        }
    };

    const drain = () => {
        // Blocks are separated by a blank line; \r\n is tolerated for good measure.
        const blocks = buffer.split(/\r?\n\r?\n/);
        buffer = blocks.pop() || '';
        for (const block of blocks) {
            if (finished) return;
            const parsed = parseEventBlock(block);
            if (parsed) handleEvent(parsed);
        }
    };

    try {
        while (!finished) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            drain();
        }

        if (!finished) {
            // Flush a trailing block the server didn't terminate with a blank line.
            buffer += '\n\n';
            drain();
        }

        if (!finished) {
            if (sawReply) {
                if (onDone) onDone();
            } else if (onNoReply) {
                onNoReply();
            } else if (onDone) {
                onDone();
            }
        }
    } catch (error) {
        if (error.name !== 'AbortError') {
            if (onError)
                onError({ message: error.message, errorCode: ASSISTANT_ERROR_CODES.AE0007 });
            throw error;
        }
    }
};

/**
 * Hands a question to the Privacy Team. Escalation creates its own conversation, so
 * the conversation passed here is the one escalated *from*, not the one being made —
 * the caller navigates to the returned `conversationId`, whose transcript already
 * carries the first message. Nothing in the transcript says the AI is paused — the
 * escalation's `status` is what the widget renders that from.
 *
 * Only a uuid goes out as the source: chat-api resolves it within the caller's own
 * conversations before filing anything, so the pending-id sentinel a not-yet-saved
 * conversation carries would 422 the whole raise over a link.
 *
 * @param {string|null} conversationId The conversation being escalated from, if any
 * @param {{subject: string, text: string}} payload
 * @returns {Promise<object>}
 */
export const escalateConversation = (conversationId, payload) =>
    chatApiJson('/v1/escalations', {
        method: 'POST',
        body: {
            title: payload.subject,
            message: payload.text,
            metadata: { locale: locale() },
            ...(conversationId && isUuid(conversationId)
                ? // eslint-disable-next-line camelcase -- chat-api's own field name
                  { source_conversation_id: conversationId }
                : {}),
        },
    }).then(formatEscalation);

/**
 * @param {string} conversationId
 * @returns {Promise<null>}
 */
export const deleteConversation = conversationId =>
    chatApiJson(`/v1/conversations/${conversationId}`, { method: 'DELETE' });

/**
 * Upserts the caller's feedback on a message. The widget speaks in positive/negative;
 * chat-api stores a signed sentiment.
 *
 * @param {string} _conversationId Ignored; the message id identifies the target
 * @param {string} messageId
 * @param {'positive'|'negative'} sentiment
 * @returns {Promise<object>}
 */
export const submitFeedback = (_conversationId, messageId, sentiment) =>
    chatApiJson(`/v1/messages/${messageId}/feedback`, {
        method: 'PUT',
        body: { sentiment: sentiment === 'positive' ? 1 : -1 },
    });

/**
 * Renames or archives a conversation. Unused by the widget today; chat-api's PATCH is
 * the only way to set a title, so it is here for the callers that will need it.
 *
 * @param {string} conversationId
 * @param {{title?: string|null, status?: 'active'|'archived'}} changes
 * @returns {Promise<object>}
 */
export const updateConversation = (conversationId, changes) =>
    chatApiJson(`/v1/conversations/${conversationId}`, {
        method: 'PATCH',
        body: changes,
    }).then(formatConversation);
